import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { replaceResponseVariables } from '../api/controllers/responseVariable';
import ResponseVariableReplacementRequest from '../models/responseVariables/ResponseVariableReplacementRequest';
import {
    color_colors_ocean,
    color_colors_royalty,
    color_colors_growth,
    color_colors_decline,
    color_colors_gold,
    color_colors_sky,
    color_shades_darker
} from '../constants/colors';

/**
 * Represents a detected variable in the text
 */
export interface DetectedVariable {
    /** Full variable reference including prefix (e.g., "session.name") */
    fullReference: string;
    /** Variable category/prefix (e.g., "session", "constant", "user") */
    category: string;
    /** Variable path/name after the prefix (e.g., "name", "user-info.d_o_b") */
    path: string;
}

/**
 * Represents the value state for a variable field
 */
export type VariableValueState = 'null' | 'undefined' | 'string' | 'number' | 'boolean';

export interface VariableFieldValue {
    state: VariableValueState;
    stringValue: string;
    numberValue: number;
    booleanValue: boolean;
}

interface UseResponseVariableTesterOptions {
    initialText?: string;
    debounceMs?: number;
}

interface UseResponseVariableTesterResult {
    // State
    inputText: string;
    setInputText: (text: string) => void;
    variableValues: Map<string, VariableFieldValue>;
    isLoading: boolean;
    replacedText: string | null;
    error: string | null;
    detectedVariables: DetectedVariable[];
    
    // Handlers
    handleValueStateChange: (fullReference: string, state: VariableValueState) => void;
    handleStringValueChange: (fullReference: string, value: string) => void;
    handleNumberValueChange: (fullReference: string, value: number) => void;
    handleBooleanValueChange: (fullReference: string, value: boolean) => void;
    
    // Utilities
    getCategoryColor: (category: string) => string;
    reset: () => void;
}

/**
 * Custom hook that provides all the logic for testing response variables.
 * Used by both ResponseVariableTestSettings and ResponseVariableTestModal.
 */
export const useResponseVariableTester = (options: UseResponseVariableTesterOptions = {}): UseResponseVariableTesterResult => {
    const { initialText = '', debounceMs = 500 } = options;
    
    const [inputText, setInputText] = useState<string>(initialText);
    const [variableValues, setVariableValues] = useState<Map<string, VariableFieldValue>>(new Map());
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [replacedText, setReplacedText] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

    /**
     * Detects response variables in the input text.
     * Variables follow the pattern: prefix.path where prefix can be:
     * - session, user, constant, variable, prompt, slot, nlp, feature
     * And path can contain dots, hyphens, and underscores
     */
    const detectedVariables = useMemo<DetectedVariable[]>(() => {
        if (!inputText) return [];

        const variablePattern = /\b(session|user|constant|variable|prompt|slot|nlp|feature)\.([a-zA-Z0-9_\-\.]+)/g;
        
        const variables: DetectedVariable[] = [];
        const seen = new Set<string>();
        
        let match;
        while ((match = variablePattern.exec(inputText)) !== null) {
            const fullReference = match[0];
            if (!seen.has(fullReference)) {
                seen.add(fullReference);
                variables.push({
                    fullReference,
                    category: match[1],
                    path: match[2]
                });
            }
        }

        return variables;
    }, [inputText]);

    // Initialize new variable values when detected variables change
    useEffect(() => {
        setVariableValues(prevValues => {
            const newValues = new Map(prevValues);
            
            // Add new variables with default state
            detectedVariables.forEach(variable => {
                if (!newValues.has(variable.fullReference)) {
                    newValues.set(variable.fullReference, { 
                        state: 'string', 
                        stringValue: '', 
                        numberValue: 0, 
                        booleanValue: false 
                    });
                }
            });

            // Remove variables that are no longer detected
            const currentRefs = new Set(detectedVariables.map(v => v.fullReference));
            for (const key of newValues.keys()) {
                if (!currentRefs.has(key)) {
                    newValues.delete(key);
                }
            }

            return newValues;
        });
    }, [detectedVariables]);

    const handleValueStateChange = useCallback((fullReference: string, state: VariableValueState) => {
        setVariableValues(prev => {
            const newValues = new Map(prev);
            const current = newValues.get(fullReference);
            newValues.set(fullReference, { 
                state, 
                stringValue: current?.stringValue || '',
                numberValue: current?.numberValue ?? 0,
                booleanValue: current?.booleanValue ?? false
            });
            return newValues;
        });
    }, []);

    const handleStringValueChange = useCallback((fullReference: string, value: string) => {
        setVariableValues(prev => {
            const newValues = new Map(prev);
            const current = newValues.get(fullReference);
            newValues.set(fullReference, { 
                state: 'string', 
                stringValue: value,
                numberValue: current?.numberValue ?? 0,
                booleanValue: current?.booleanValue ?? false
            });
            return newValues;
        });
    }, []);

    const handleNumberValueChange = useCallback((fullReference: string, value: number) => {
        setVariableValues(prev => {
            const newValues = new Map(prev);
            const current = newValues.get(fullReference);
            newValues.set(fullReference, { 
                state: 'number', 
                stringValue: current?.stringValue || '',
                numberValue: value,
                booleanValue: current?.booleanValue ?? false
            });
            return newValues;
        });
    }, []);

    const handleBooleanValueChange = useCallback((fullReference: string, value: boolean) => {
        setVariableValues(prev => {
            const newValues = new Map(prev);
            const current = newValues.get(fullReference);
            newValues.set(fullReference, { 
                state: 'boolean', 
                stringValue: current?.stringValue || '',
                numberValue: current?.numberValue ?? 0,
                booleanValue: value
            });
            return newValues;
        });
    }, []);

    /**
     * Sets a nested value in an object using dot-notation path
     */
    const setNestedValue = (obj: { [key: string]: any }, path: string, value: any) => {
        const parts = path.split('.');
        let current = obj;
        
        for (let i = 0; i < parts.length - 1; i++) {
            const part = parts[i];
            if (!current[part] || typeof current[part] !== 'object') {
                current[part] = {};
            }
            current = current[part];
        }
        
        current[parts[parts.length - 1]] = value;
    };

    const buildRequest = useCallback((): ResponseVariableReplacementRequest => {
        const request: ResponseVariableReplacementRequest = {
            text: inputText,
            variables: [],
            sessionAttributes: {},
            userAttributes: {},
            slots: {}
        };

        detectedVariables.forEach(variable => {
            const fieldValue = variableValues.get(variable.fullReference);
            if (!fieldValue) return;

            // Determine the actual value based on state
            let actualValue: any;
            if (fieldValue.state === 'null') {
                actualValue = null;
            } else if (fieldValue.state === 'undefined') {
                // For undefined, we simply don't include the value
                return;
            } else if (fieldValue.state === 'number') {
                actualValue = fieldValue.numberValue;
            } else if (fieldValue.state === 'boolean') {
                actualValue = fieldValue.booleanValue;
            } else {
                actualValue = fieldValue.stringValue;
            }

            // Route the value to the appropriate category
            switch (variable.category) {
                case 'constant':
                case 'variable':
                case 'prompt':
                    request.variables!.push({
                        name: variable.path,
                        value: actualValue ?? ''
                    });
                    break;
                case 'session':
                    setNestedValue(request.sessionAttributes!, variable.path, actualValue);
                    break;
                case 'user':
                    setNestedValue(request.userAttributes!, variable.path, actualValue);
                    break;
                case 'slot':
                    if (actualValue !== null) {
                        request.slots![variable.path] = actualValue;
                    }
                    break;
                default:
                    // For nlp, feature, or unknown - add to session attributes as fallback
                    setNestedValue(request.sessionAttributes!, variable.fullReference, actualValue);
                    break;
            }
        });

        return request;
    }, [inputText, detectedVariables, variableValues]);

    // Debounced auto-run effect
    useEffect(() => {
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }

        if (!inputText.trim()) {
            setReplacedText(null);
            return;
        }

        debounceTimerRef.current = setTimeout(async () => {
            setIsLoading(true);
            setError(null);

            try {
                const request = buildRequest();
                const result = await replaceResponseVariables(request);

                if (result.resultType === 'Ok' && result.data) {
                    setReplacedText(result.data.replacedText);
                } else {
                    setError('Failed to perform variable replacement.');
                }
            } catch (err) {
                setError('An error occurred while processing the request.');
                console.error('Response variable replacement error:', err);
            } finally {
                setIsLoading(false);
            }
        }, debounceMs);

        return () => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
        };
    }, [inputText, variableValues, buildRequest, debounceMs]);

    const getCategoryColor = useCallback((category: string): string => {
        switch (category) {
            case 'session': return color_colors_ocean;
            case 'user': return color_colors_royalty;
            case 'constant':
            case 'variable':
            case 'prompt': return color_colors_growth;
            case 'slot': return color_colors_decline;
            case 'nlp': return color_colors_gold;
            case 'feature': return color_colors_sky;
            default: return color_shades_darker;
        }
    }, []);

    const reset = useCallback(() => {
        setInputText(initialText);
        setVariableValues(new Map());
        setReplacedText(null);
        setError(null);
    }, [initialText]);

    return {
        inputText,
        setInputText,
        variableValues,
        isLoading,
        replacedText,
        error,
        detectedVariables,
        handleValueStateChange,
        handleStringValueChange,
        handleNumberValueChange,
        handleBooleanValueChange,
        getCategoryColor,
        reset
    };
};

export default useResponseVariableTester;
